import { useState, useEffect } from "react";
import { Box, Flex, Image } from "rebass/styled-components";
import styled from "styled-components";

import { InfoSection } from "../InfoSection";
import { Tabs } from "MVVM/Components/Tabs";
import { Divider } from "MVVM/Components/Divider";
import { ShareSection } from "../ShareSection";
import { CampaignContentCallout } from "MVVM/Views/Competitions/Components/CampaignContentCallout";
import { ClipPagePromotion } from "MVVM/Views/ClipExperience/ClipPagePromotion";
import { IClip, TClip } from "MVVM/@types";
import { useIsTop5Clip } from "MVVM/Hooks/useIsTop5Clip";
import { ClipComments } from "MVVM/Components/Comments/Clip";
import { Feed } from "../Feed";
import { CreatorCards } from "../CreatorCards";
import { FollowList } from "../FollowList";
import { getQueryParam } from "MVVM/Utilities/QueryParam";
import { useRecoilValue } from "recoil";
import { PromoSession } from "MVVM/State/promoSession";
import { ClipContext, IClipPagePromotion } from "../@types";
import { PlayersList } from "MVVM/Components/Top5Widget/PlayersList";

interface ISidebarProps {
  clip: IClip;
  referrer: string | undefined;
  context: ClipContext | null;
  feedData?: {
    clipsRecommended: TClip[];
  };
  feedError?: Error | null;
  feedLoading?: boolean;
  onRemixClicked: () => void;
}
const { REACT_APP_S3_CLIP_BUCKET } = process.env;

const Sidebar = ({
  clip,
  context,
  feedData,
  feedError,
  feedLoading,
  onRemixClicked,
}: ISidebarProps) => {
  const tabContent = [];
  const clipInCampaign = !!clip?.campaign?.slug;
  const [commentCount, setCommentCount] = useState<number | null>(null);
  const commentsDisabled = clip.user?.commentsDisabled;
  const selectedTab = getQueryParam("tab") || context?.metadata?.tab;
  const [activeTab, setActiveTab] = useState<number>(0);
  const promoSession = useRecoilValue(PromoSession);
  const clipPagePromotions = promoSession?.activeClipPagePromos;
  const queryParamPromotion = getQueryParam("promotion");
  const isTop5Clip = useIsTop5Clip(clip, [
    "INTERNAL_FEATURED",
    "IFRAME_FEATURED",
  ]);
  const topPlayers = clip.associatedUsers || [];

  const promotionToShow = queryParamPromotion
    ? clipPagePromotions.find(
        (promo: IClipPagePromotion) => promo.name === queryParamPromotion,
      )
    : null;

  const tabs = {
    comments: {
      component: (
        <ClipComments
          clipId={clip.id}
          setCommentCount={setCommentCount}
          disabled={commentsDisabled}
        />
      ),
      name: "comments",
      title: `Comments ${
        !commentsDisabled && commentCount && commentCount > 0
          ? `(${commentCount})`
          : ""
      }`,
      type: "Comments",
    },
    creatorCards: {
      component: <CreatorCards effects={clip.effects} />,
      name: "creator-cards",
      title: "Creator Cards",
      type: "Creator Cards",
    },
    forYouFeed: {
      component: (
        <Feed
          feedData={feedData}
          feedError={feedError}
          feedLoading={feedLoading}
        />
      ),
      name: "recommended",
      title: "Recommended",
      type: "Recommended",
    },
    followList: {
      component: (
        <FollowList
          players={clip?.moment?.match?.players}
          matchId={clip?.moment?.match?.id}
        />
      ),
      name: "in-this-match",
      title: "In This Match",
      type: "In This Match",
    },
    top5List: {
      component: (
        <Box px={20} py={4}>
          <PlayersList topPlayers={topPlayers} />
        </Box>
      ),
      name: "top-5",
      title: "Top 5 Creators",
      type: "Top 5",
    },
  };

  switch (context?.type) {
    case "match":
      tabContent.push(tabs.comments, tabs.followList, tabs.creatorCards);
      break;
    case "top5":
      tabContent.push(tabs.comments, tabs.top5List);
      break;
    default:
      tabContent.push(tabs.comments, tabs.forYouFeed, tabs.followList);
      break;
  }

  const defaultTab = tabContent.findIndex((tab) => tab.name === selectedTab);

  useEffect(() => {
    if (defaultTab !== -1) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);

  const handleTabChange = (newActiveTab: number) => {
    setActiveTab(newActiveTab);
  };

  return (
    <SidebarContainer>
      {isTop5Clip && (
        <Top5Banner
          src={`${REACT_APP_S3_CLIP_BUCKET}/static/web/top5/top5sidebar.png`}
        />
      )}
      <InfoSection clip={clip} />
      {clipInCampaign && (
        <CampaignContentCallout
          compact={true}
          marketingContent={clip?.campaign?.marketing}
          slug={clip?.campaign?.slug}
          endsAt={clip?.campaign?.endsAt}
          primaryColor={clip?.campaign?.primaryColor}
          secondaryColor={clip?.campaign?.secondaryColor}
        />
      )}
      {promotionToShow && <ClipPagePromotion promotion={promotionToShow} />}

      <Divider />
      <ShareSection clip={clip} onRemixClicked={onRemixClicked} />
      <Divider />
      <Tabs
        tabs={tabContent}
        active={activeTab}
        onTabChange={handleTabChange}
      />
      <ColumnContainer>
        {activeTab === 0 ? (
          <>
            <ClipComments
              clipId={clip.id}
              setCommentCount={setCommentCount}
              disabled={commentsDisabled}
            />
          </>
        ) : (
          tabContent[activeTab]?.component
        )}
      </ColumnContainer>
    </SidebarContainer>
  );
};

const SidebarContainer = styled(Flex)`
  background: ${({ theme }) => theme.colors.darkerNed};
  flex-direction: column;
  scrollbar-width: none;
  width: 441px;
  z-index: 2;

  @media (max-width: ${(props) => props.theme.breaks.theater}) {
    flex-grow: 1;
    overflow: scroll;
    width: 100%;
  }
`;

const ColumnContainer = styled(Box)`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  width: 100%;
`;

const Top5Banner = styled(Image)`
  @media (max-width: ${(props) => props.theme.breaks.theater}) {
    display: none;
  }
`;

export { Sidebar };
